/**
 * Which charts share a crosshair, and what is allowed to travel between them.
 *
 * ## Groups, not one switch
 *
 * The question a trader is answering is "sync THESE two", not "sync
 * everything". A colour tag per chart is the model every platform that does
 * this uses, and it is the only one that expresses two independent pairs on
 * one screen — ES and NQ following each other on the left, CL left alone on
 * the right.
 *
 * ## Time always travels. Price only sometimes.
 *
 * This is the decision worth stating, because getting it wrong produces a
 * crosshair that looks right and means nothing.
 *
 * **Time is comparable across anything.** 09:31:14 on a 1-minute ES chart is
 * the same instant as 09:31:14 on a 15-minute CL chart. Different timeframes
 * are fine too — the receiving chart snaps to whichever of its own bars
 * contains that instant, which is exactly what a reader wants.
 *
 * **Price is only comparable on the same instrument.** ES at 6042.25 and CL at
 * 6042.25 are not a relationship; one is an index future near six thousand and
 * the other is crude oil, which has never traded there. A horizontal line
 * drawn at "the same price" across two instruments is a coincidence of digits
 * rendered as a fact, and a trader glancing at it reads a level.
 *
 * So price travels only between panes resolving to the SAME symbol, and
 * `withPrice` says so per target rather than per group — one group can hold
 * two ES charts and a CL chart, and the answer differs for each pair.
 */
import type { PanelKind } from './panels.js';
import type { Workspace } from './layout.js';
import { isSyncGroup } from './layout.js';

/** Where a crosshair may be echoed, and how much of it. */
export interface CrosshairTarget {
  readonly paneId: string;
  /**
   * True when the price may travel as well as the time.
   *
   * Same instrument only — see the header. A renderer that ignores this and
   * always applies price draws a level on the wrong chart.
   */
  readonly withPrice: boolean;
}

/**
 * The instrument a pane is actually showing.
 *
 * `defaultSymbol` is not a convenience: a pane with no symbol of its own is
 * following the application, and a pane that was deliberately pointed at the
 * same instrument is showing the same thing. Comparing the raw stored values
 * would call those two different and silently stop price from travelling
 * between two charts of one instrument.
 */
export function paneSymbol(
  ws: Workspace, paneId: string, defaultSymbol?: string | null,
): string | null {
  for (const column of ws.columns) {
    for (const pane of column.panes) {
      if (pane.id === paneId) return pane.config?.symbol ?? defaultSymbol ?? null;
    }
  }
  return null;
}

/** The sync group a pane is in, or null for a pane in none. */
export function paneSyncGroup(ws: Workspace, paneId: string): string | null {
  for (const column of ws.columns) {
    for (const pane of column.panes) {
      if (pane.id !== paneId) continue;
      const group = pane.config?.syncGroup;
      // `none` is stored as absent by `normaliseWorkspace`, but a value that
      // arrived from somewhere else is checked rather than trusted.
      return isSyncGroup(group) && group !== 'none' ? group : null;
    }
  }
  return null;
}

/**
 * Every pane a crosshair on `sourcePaneId` should be echoed to.
 *
 * Excludes the source itself, always. That is structural rather than tidy: a
 * renderer echoing a position back to the chart it came from re-triggers that
 * chart's own move handler, which echoes again, and the loop runs until the
 * pointer stops. Making the source unreachable from its own target list means
 * the naive implementation cannot build that loop.
 *
 * Detached panes are excluded too. They are in another OS window with no
 * shared pointer, and a crosshair sent to one would sit wherever it was last
 * put — a stationary line on a second monitor that looks like a drawing.
 */
export function crosshairTargets(
  ws: Workspace, sourcePaneId: string, defaultSymbol?: string | null,
): readonly CrosshairTarget[] {
  const group = paneSyncGroup(ws, sourcePaneId);
  if (group === null) return [];

  const sourceSymbol = paneSymbol(ws, sourcePaneId, defaultSymbol);
  const out: CrosshairTarget[] = [];

  for (const column of ws.columns) {
    for (const pane of column.panes) {
      if (pane.id === sourcePaneId) continue;
      // Charts only. Nothing else in the panel registry draws a crosshair, and
      // sending one to a panel that cannot show it is a silent no-op that
      // looks like the group not working.
      if (!isCrosshairPanel(pane.kind)) continue;
      if (paneSyncGroup(ws, pane.id) !== group) continue;

      const symbol = paneSymbol(ws, pane.id, defaultSymbol);
      out.push({
        paneId: pane.id,
        // Both known AND equal. Two panes whose symbol cannot be resolved are
        // not thereby the same instrument.
        withPrice: symbol !== null && sourceSymbol !== null && symbol === sourceSymbol,
      });
    }
  }
  return out;
}

/**
 * Whether a panel has a crosshair to share.
 *
 * Charts, and only charts. Deliberately a check in one place rather than a
 * flag on `PanelSpec`: `multiple` earned a column because five kinds set it
 * differently, and a field that would read `true` for exactly one panel is
 * speculative generality. If the footprint ever grows a time axis with a
 * crosshair, this is the line that changes.
 */
function isCrosshairPanel(kind: PanelKind): boolean {
  return kind === 'chart';
}
